import React, { useState, useEffect } from 'react';
import { leaveAPI } from '../../services/api';

const LEAVE_TYPES = [
  { key: 'annual', label: 'Annual', color: 'text-indigo-600', bg: 'bg-indigo-50' },
  { key: 'sick', label: 'Sick', color: 'text-rose-600', bg: 'bg-rose-50' },
  { key: 'study', label: 'Study', color: 'text-purple-600', bg: 'bg-purple-50' },
  { key: 'family', label: 'Family', color: 'text-amber-600', bg: 'bg-amber-50' },
];

const BalanceBar = ({ used, total }) => {
  if (!total || total === 0) return null;
  const pct = Math.min((used / total) * 100, 100);
  const color = pct >= 80 ? 'bg-rose-400' : pct >= 50 ? 'bg-amber-400' : 'bg-teal-400';
  return (
    <div className="w-full bg-slate-100 rounded-full h-1.5 mt-1">
      <div className={`h-1.5 rounded-full ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
};

const InternOverviewTable = () => {
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedIntern, setExpandedIntern] = useState(null);

  useEffect(() => {
    fetchBalances();
  }, []);

  const fetchBalances = async () => {
    try {
      const response = await leaveAPI.getTeamBalances();
      setBalances(response.data);
    } catch (error) {
      console.error('Failed to load intern overview');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <p className="text-sm text-slate-400">Loading...</p>;

  return (
    <div className="space-y-3">
      {balances.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-6 text-center text-sm text-slate-400">
          No interns assigned
        </div>
      ) : (
        balances.map((intern) => (
          <div key={intern.id} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            {/* Intern Header Row */}
            <button
              onClick={() => setExpandedIntern(expandedIntern === intern.id ? null : intern.id)}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors text-left"
            >
              <div>
                <p className="text-sm font-medium text-slate-900">{intern.full_name}</p>
                <p className="text-xs text-slate-400 mt-0.5">{intern.email}</p>
                {intern.department && (
                  <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full mt-1 inline-block">
                    {intern.department}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-6 shrink-0">
                {/* Quick summary dots */}
                <div className="hidden sm:flex gap-4">
                  {LEAVE_TYPES.map((type) => (
                    <div key={type.key} className="text-center">
                      <p className={`text-xs font-medium ${type.color}`}>{type.label}</p>
                      <p className="text-sm font-semibold text-slate-900">
                        {intern[type.key]?.remaining ?? 0}
                        <span className="text-xs font-normal text-slate-400"> left</span>
                      </p>
                    </div>
                  ))}
                </div>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className={`w-4 h-4 text-slate-400 transition-transform ${expandedIntern === intern.id ? 'rotate-180' : ''}`}
                >
                  <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </button>

            {/* Expanded Detail */}
            {expandedIntern === intern.id && (
              <div className="border-t border-slate-100 px-5 py-4 bg-slate-50">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {LEAVE_TYPES.map((type) => {
                    const data = intern[type.key];
                    const total = data?.balance ?? 0;
                    const used = data?.used ?? 0;
                    const remaining = data?.remaining ?? 0;
                    return (
                      <div key={type.key} className="bg-white rounded-lg border border-slate-200 p-4">
                        <div className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium mb-3 ${type.bg} ${type.color}`}>
                          {type.label} Leave
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-xs">
                            <span className="text-slate-500">Balance</span>
                            <span className="font-medium text-slate-700">{total} days</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-slate-500">Used</span>
                            <span className="font-medium text-rose-600">{used} days</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-slate-500">Remaining</span>
                            <span className="font-medium text-teal-600">{remaining} days</span>
                          </div>
                          <BalanceBar used={used} total={total} />
                          <p className="text-xs text-slate-400">
                            {total > 0 ? `${Math.round((used / total) * 100)}% used` : 'No allocation'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default InternOverviewTable;